/**
 * H3UpscaleToolbar — MiniMax H3「升 2K」工具条组件
 *
 * 紫色胶囊，显示在 H3 生成的 768P 视频节点上方。
 *
 * ── 胶囊颜色方案（区分四种工具条）──
 *   MJ Grid（红色）:     #3d1a1e — 四宫格操作
 *   MJ Upscaled（黄色）: #3d2e12 — 高清图操作
 *   GPT Inpaint（蓝色）: #0c2d4d — 局部重绘
 *   H3 升 2K（紫色）:    #2a1b4d — 视频超分     ← 本组件
 *
 * ── 布局 ──
 *   [Logo] [工具箱名] | [升 2K] | [✦ 积分]
 *
 * ── 显示条件（在 CustomNode.js 中判断）──
 *   isNodeActive && isVideoNode && !loading && hasOutput
 *   && vendor === 'apimart-minimax-h3' && vendorTaskId
 *   && params.resolution !== '2K' && h3UpscaleConfig.enabled
 *
 * ── Props ──
 *   nodeId       — 当前节点 ID
 *   onAction     — 点击「升 2K」的回调 → 触发 useH3Upscale.handleH3Upscale
 *   zoom         — 画布缩放倍率（用于抗缩放定位）
 *   toolboxName  — 管理员配置的工具箱名称
 *   toolboxLogo  — 管理员配置的工具箱 Logo URL
 *   creditCost   — 每次调用消耗的积分数
 */
import React, { useState } from 'react';
import { Sparkle } from 'lucide-react';
import QuickTooltip from '../components/QuickTooltip';

// 默认 logo：向上箭头（升级语义）
const UpscaleLogo = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
    <path d="M12 4L18 11H14V20H10V11H6Z" fill="currentColor" opacity="0.9" />
  </svg>
);

function ToolButton({ label, onClick }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      className="nodrag"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      title={label}
      style={{
        display: 'flex', alignItems: 'center', gap: 3,
        padding: '2px 8px', borderRadius: 4, cursor: 'pointer',
        background: hover ? 'rgba(255,255,255,0.15)' : 'transparent',
        transition: 'background 0.15s',
        fontSize: 10, color: 'rgba(255,255,255,0.9)',
        whiteSpace: 'nowrap',
      }}
    >
      <span>{label}</span>
    </div>
  );
}

export default function H3UpscaleToolbar({ nodeId, onAction, zoom = 1, toolboxName, toolboxLogo, creditCost = 0 }) {
  const capsuleBar = {
    display: 'flex', alignItems: 'center', gap: 1,
    background: '#2a1b4d',
    border: '1px solid rgba(167, 139, 250, 0.3)',
    borderRadius: '50px', padding: '4px 10px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
    pointerEvents: 'auto', width: 'max-content',
    userSelect: 'none',
    backdropFilter: 'blur(8px)',
  };

  const divider = { width: 1, height: 14, background: 'rgba(255,255,255,0.2)', margin: '0 3px' };

  // 定位逻辑与 MjToolbar / GptInpaintToolbar 一致
  const titleVisualTop = 2 - 22 / zoom;
  const dynamicTop = titleVisualTop - 5 - 30;

  return (
    <div className="nodrag" style={{
      position: 'absolute',
      top: dynamicTop,
      left: '50%',
      transform: `translateX(-50%) scale(${1 / zoom})`,
      transformOrigin: 'bottom center',
      zIndex: 20,
    }}>
      <div style={capsuleBar}>
        {/* Logo + 标题 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginRight: 2 }}>
          {toolboxLogo ? (
            <img src={toolboxLogo} alt="" style={{ width: 14, height: 14, objectFit: 'contain', borderRadius: 3, flexShrink: 0 }} onError={e => { e.target.style.display = 'none'; }} />
          ) : (
            <UpscaleLogo />
          )}
          <span style={{ fontSize: 9, fontWeight: 600, color: 'rgba(255,255,255,0.85)', whiteSpace: 'nowrap' }}>
            {toolboxName || 'MiniMax H3 升 2K'}
          </span>
        </div>

        <div style={divider} />

        <QuickTooltip content="保持画面不变，只提清晰度" position="top" delay={300}>
          <ToolButton label="升 2K" onClick={() => onAction?.(nodeId)} />
        </QuickTooltip>

        {/* 积分显示 */}
        {creditCost > 0 && (
          <>
            <div style={divider} />
            <QuickTooltip content="查看积分明细" position="top" delay={200}>
              <div
                onClick={() => window.dispatchEvent(new CustomEvent('open-credit-logs'))}
                style={{
                  fontSize: 9, color: 'rgba(255,255,255,0.7)', background: 'rgba(255,255,255,0.06)',
                  padding: '2px 6px', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 2,
                  cursor: 'pointer', transition: 'background 0.15s',
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.12)'}
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'}
              >
                <Sparkle size={9} color="rgba(255,255,255,0.7)" strokeWidth={2.5} style={{ marginRight: 1 }} /> {creditCost}
              </div>
            </QuickTooltip>
          </>
        )}
      </div>
    </div>
  );
}
